import { BLUE, CHROME, INK, PRIMARY } from '../../Theme/palette';
import { ToolOutlined } from '@ant-design/icons';
import {
  InventoryUploadDrawer,
  InventoryUploadDrawerHandle,
  ServiceDetailsCard,
  SharedEmpty,
  ServicePickerDrawer,
  ServiceSelect,
} from '@components';
import { useInventoryCategories, useRoleAccess } from '@hooks';
import { useStore } from '@stores';
import { Utils } from '@utils';
import { Badge, Typography } from 'antd';
import { runInAction } from 'mobx';
import { observer } from 'mobx-react';
import React, { useRef, useState } from 'react';
import { IOrderServiceSelectProps } from './OrderServiceSelect.props';
import { ReturnPartsModal } from './ReturnPartsModal';

const { Text } = Typography;

const sectionStyle: React.CSSProperties = {
  background: `linear-gradient(135deg, ${CHROME.bg} 0%, ${CHROME.bgLight} 100%)`,
  borderRadius: 10,
  border: `1px solid ${CHROME.border}`,
  padding: '12px 8px 8px',
  marginBottom: 8,
};

const sectionHeaderStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  marginBottom: 10,
};

const sectionTitleStyle: React.CSSProperties = {
  fontSize: 14,
  fontWeight: 700,
  color: INK,
};

const iconWrapperStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 24,
  height: 24,
  borderRadius: 5,
  background: PRIMARY,
  color: '#fff',
  fontSize: 12,
};

const OrderServiceSelectMobile: React.FC<IOrderServiceSelectProps> = ({
  atLeastOneService,
  disabled,
  variant = 'selected',
  basket,
  hideHeader,
  hideSavedOnlyActions,
  pickerOpen,
  onPickerClose,
}) => {
  const { serviceStore, orderStore } = useStore();
  const { order: access } = useRoleAccess();
  const currentOrderId = orderStore.order?.id;
  const { options: categoryOptions } = useInventoryCategories();
  const uploadDrawerRef = useRef<InventoryUploadDrawerHandle>(null);
  const [returnPartsOpen, setReturnPartsOpen] = useState(false);
  const isAdditional = variant === 'additional';
  const isRevision = variant === 'revision';
  // A basket has no additional/revision sets - there is only ever the one job in it.
  const isBasket = !!basket;
  // Returns run against a saved order's extras, so they are meaningless for a job not yet created.
  const canReturnParts =
    !isAdditional && !isRevision && access.partsActions && !isBasket && !hideSavedOnlyActions;

  const services = basket
    ? basket.selectedServices
    : isAdditional
      ? serviceStore.additionalSelectedServices
      : isRevision
        ? serviceStore.revisionSelectedServices
        : serviceStore.selectedServices;

  const pricingSummary = basket
    ? basket.pricingSummary
    : isRevision
      ? serviceStore.revisionPricingSummary
      : serviceStore.pricingSummary;

  const handleUploadSuccess = (createdItems: any[], parsed?: any[]) => {
    if (basket) {
      basket.addPartsFromInventoryBulk(createdItems, parsed);
      return;
    }
    serviceStore.addPartsFromInventoryBulk(
      createdItems,
      parsed,
      isAdditional ? 'additional' : isRevision ? 'revision' : 'selected'
    );
  };

  // For the order being edited, keep its end time anchored to the total service duration.
  // A basket has no saved order behind it, so there is nothing to anchor.
  const syncOrderEndTime = () => {
    if (isBasket || variant !== 'selected') return;
    const totalHours = serviceStore.selectedServices.reduce((acc, s) => acc + (s.duration || 0), 0);
    orderStore.syncEndFromServiceDuration(totalHours);
  };

  // With one service required, the last one cannot be removed.
  const canRemoveService = !(atLeastOneService && services.length === 1);

  const handleSelect = (id: number) => {
    if (basket) basket.addService(id);
    else if (isAdditional) serviceStore.addAdditionalService(id);
    else if (isRevision) serviceStore.addRevisionService(id);
    else serviceStore.addService(id);
    syncOrderEndTime();
  };

  const openUploadParts = () =>
    uploadDrawerRef.current?.open({
      orderId: currentOrderId,
      serviceOptions: services.map((s) => ({ id: s.id, name: Utils.formatServiceLabel(s) })),
    });

  const handleDeselect = (id: number) => {
    if (basket) basket.removeService(id, atLeastOneService);
    else if (isAdditional) serviceStore.removeAdditionalService(id);
    else if (isRevision) serviceStore.removeRevisionService(id, atLeastOneService);
    else serviceStore.removeService(id, atLeastOneService);
    syncOrderEndTime();
  };

  const handleClear = () => {
    if (basket) basket.clearSelectedServices();
    else if (isAdditional) serviceStore.clearAdditionalSelectedServices();
    else if (isRevision) serviceStore.clearRevisionSelectedServices();
    else serviceStore.clearSelectedServices();
    syncOrderEndTime();
  };

  // One element, used by both returns below. Built here rather than inline in each: as two
  // separate elements React unmounted one and mounted the other the moment the first service
  // landed, which tore the open drawer down and rebuilt it - the list reloaded and the screen
  // flashed. Every service after the first stayed in the same branch, so only the first flickered.
  const picker = onPickerClose ? (
    <ServicePickerDrawer
      open={!!pickerOpen}
      mode="multiple"
      showMyServicesOnly
      title="Add work"
      selectedIds={services.map((x) => x.id)}
      selectedLabels={Object.fromEntries(services.map((x) => [x.id, Utils.formatServiceLabel(x)]))}
      onClose={onPickerClose}
      onSelect={(service) => {
        // addService resolves the id through the shared lookup, as the inline select does before
        // it raises onSelect - the picker hands over the full service instead.
        runInAction(() => {
          serviceStore.selectServices.set(service.id, service);
        });
        handleSelect(service.id);
      }}
      onDeselect={handleDeselect}
      onClear={!atLeastOneService ? handleClear : undefined}
    />
  ) : null;

  return (
    <>
      {/* Outside both bodies below. As a child of one of them, picking the first service swapped
          the body - the empty state for the list - and React tore the open drawer down with it. */}
      {picker}
      {onPickerClose && services.length === 0 ? (
        <SharedEmpty description="No work on this job yet - use Add work to pick the services" />
      ) : (
    <div style={sectionStyle}>
      {!hideHeader && (
      <div style={{ ...sectionHeaderStyle, flexWrap: 'wrap', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={iconWrapperStyle}>
            <ToolOutlined />
          </div>
          <Text style={sectionTitleStyle}>Services</Text>
          {services.length > 0 && (
            <Badge
              count={services.length}
              style={{ backgroundColor: PRIMARY, fontSize: 10, fontWeight: 600 }}
            />
          )}
        </div>
        {(!isAdditional || isRevision) && services.length > 0 && access.totals && (
          <span
            style={{
              fontSize: 13,
              fontWeight: 700,
              color: BLUE.tag,
              background: BLUE.soft,
              border: `1px solid ${BLUE.card}`,
              padding: '2px 10px',
              borderRadius: 4,
            }}
          >
            {Utils.formatCurrency(pricingSummary.totalPrice)}
          </span>
        )}
      </div>
      )}

      {/* The inline select; when the host drives the picker there is nothing here - the drawer is
          mounted once at the top of the component. */}
      {!onPickerClose && (
      <div style={{ marginBottom: services.length > 0 ? 8 : 0 }}>
        <ServiceSelect
          mode="multiple"
          showMyServicesOnly
          allowClear={!atLeastOneService}
          value={services.map((x) => x.id)}
          defaultValue={services.map((x) => ({
            label: Utils.formatServiceLabel(x),
            value: x.id,
          }))}
          onClear={handleClear}
          onSelect={handleSelect}
          onDeselect={handleDeselect}
          disabled={disabled}
        />
      </div>
      )}

      {services.map((selectedService) => (
        <ServiceDetailsCard
          key={selectedService.id}
          service={selectedService}
          disabled={disabled}
          variant={variant}
          basket={basket}
          onRemove={() => handleDeselect(selectedService.id)}
          canRemove={canRemoveService}
          onUploadParts={access.partsActions ? openUploadParts : undefined}
          onReturnParts={canReturnParts ? () => setReturnPartsOpen(true) : undefined}
        />
      ))}

      <ReturnPartsModal open={returnPartsOpen} onClose={() => setReturnPartsOpen(false)} />

      <InventoryUploadDrawer
        ref={uploadDrawerRef}
        categoryOptions={categoryOptions}
        onSuccess={handleUploadSuccess}
      />
    </div>
      )}
    </>
  );
};

export default observer(OrderServiceSelectMobile);
